import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import ClientPicker from "./ClientPicker";
import {
  createPartnerDocumentRequest,
  shareDocumentRequestWithClient,
} from "../../api/endpoints/documents";
import { buildRequestPayload, emptyRequestItem, validateRequestForm } from "./newRequestHelpers";

const errorMessage = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || err?.message || fallback;

/**
 * Ask a client for documents, like the office does: name each document, then email the client
 * the secure upload link. Leaving "email the link" off creates the list for the partner to fill.
 */
/** Pass a client to fix it (a client's page); leave it out to choose one (the Documents page). */
const RequestFromClientDialog = ({
  clientId: fixedClientId,
  clientName: fixedClientName,
  clientEmail: fixedClientEmail,
  open,
  onClose,
  onCreated,
}) => {
  const [picked, setPicked] = useState(null);
  const clientId = fixedClientId ?? picked?.clientId;
  const clientName = fixedClientName ?? picked?.name;
  const clientEmail = fixedClientEmail ?? picked?.email;
  const [title, setTitle] = useState("");
  const [items, setItems] = useState(() => [emptyRequestItem()]);
  const [sendLink, setSendLink] = useState(true);
  const [formError, setFormError] = useState(null);
  const [sentLink, setSentLink] = useState(null);

  const reset = () => {
    setTitle("");
    setItems([emptyRequestItem()]);
    setSendLink(true);
    setFormError(null);
    setPicked(null);
    setSentLink(null);
  };

  const mutation = useMutation(
    async () => {
      const created = await createPartnerDocumentRequest(
        clientId,
        buildRequestPayload({ title, items })
      );
      const link = sendLink ? await shareDocumentRequestWithClient(created.id) : null;
      return { created, link };
    },
    {
      onSuccess: ({ created, link }) => {
        onCreated?.(created);
        if (link) {
          setSentLink(link);
        } else {
          toast.success("Document list created");
          reset();
          onClose();
        }
      },
      onError: (err) => toast.error(errorMessage(err, "Could not create the request")),
    }
  );

  const close = () => {
    if (mutation.isLoading) return;
    reset();
    onClose();
  };

  const updateItem = (key, changes) =>
    setItems((current) =>
      current.map((item) => (item.key === key ? { ...item, ...changes } : item))
    );

  const submit = () => {
    const error = clientId ? validateRequestForm({ title, items }) : "Choose a client first.";
    setFormError(error);
    if (!error) mutation.mutate();
  };

  if (sentLink) {
    return (
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Link sent to {clientName || "the client"}</DialogTitle>
        <DialogContent>
          <p className="text-sm text-slate-600 m-0">
            {clientEmail ? `We emailed it to ${clientEmail}. ` : ""}They verify with a one-time
            code, and what they upload shows on this client marked “From client”.
          </p>
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 break-all">
            {sentLink}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              navigator?.clipboard?.writeText(sentLink);
              toast.success("Link copied");
            }}
          >
            Copy link
          </Button>
          <Button variant="contained" disableElevation onClick={close}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        {clientName ? `Request documents from ${clientName}` : "Request documents from a client"}
      </DialogTitle>
      <DialogContent>
        {fixedClientId ? null : (
          <ClientPicker
            value={picked}
            onChange={setPicked}
            disabled={mutation.isLoading}
            error={!clientId && formError ? formError : null}
          />
        )}
        <TextField
          label="Request title"
          placeholder="e.g. Visa application documents"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          fullWidth
          size="small"
          margin="dense"
          autoFocus={Boolean(fixedClientId)}
        />

        <p className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Documents to collect
        </p>
        <ul className="list-none m-0 p-0 space-y-2">
          {items.map((item, index) => (
            <li key={item.key} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-start gap-2">
                <TextField
                  label={`Document ${index + 1}`}
                  placeholder="e.g. Passport"
                  value={item.label}
                  onChange={(event) => updateItem(item.key, { label: event.target.value })}
                  fullWidth
                  size="small"
                />
                <IconButton
                  size="small"
                  aria-label="Remove document"
                  disabled={items.length === 1}
                  onClick={() => setItems((current) => current.filter((i) => i.key !== item.key))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
              <TextField
                label="Instructions for the client (optional)"
                placeholder="e.g. Colour scan of the photo page"
                value={item.instructions}
                onChange={(event) => updateItem(item.key, { instructions: event.target.value })}
                fullWidth
                size="small"
                sx={{ mt: 1.5 }}
              />
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={item.required}
                    onChange={(event) => updateItem(item.key, { required: event.target.checked })}
                  />
                }
                label={<span className="text-sm">Required</span>}
              />
            </li>
          ))}
        </ul>
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setItems((current) => [...current, emptyRequestItem()])}
          sx={{ mt: 1 }}
        >
          Add document
        </Button>

        <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={sendLink}
                onChange={(event) => setSendLink(event.target.checked)}
              />
            }
            label={
              <span className="text-sm">
                Email the secure upload link to {clientEmail || "the client"} now
              </span>
            }
          />
        </div>

        {formError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formError}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={mutation.isLoading}>
          {mutation.isLoading ? "Sending…" : sendLink ? "Create and send link" : "Create list"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestFromClientDialog;
